import Box from '@mui/material/Box';
import Container from '@mui/material/Container';

import {
  PageIntroCopy,
  type PageIntroCopyProps,
  PageIntroSlideshow,
  PageIntroTrustStrip,
} from '@/components/molecules';
import { type PageIntroFeatureLayout } from '@/hooks/usePageIntroLayout';

import { BAND_EDGE, BAND_PADDING, BAND_SURFACE, STRIP_GAP } from './pageIntroBand';

/** Keep the title clear of the carousel, including on narrow catalogue screens. */
const COPY_WIDTH = { xs: '78%', md: '62%', lg: '58%' };

type Props = PageIntroCopyProps &
  Pick<PageIntroFeatureLayout, 'highlights' | 'slides' | 'onPhone'> & { compact?: boolean };

/**
 * The opening band: media cross-fades behind the page's own words.
 *
 * Read top to bottom it is where you are, what this page is, what it offers,
 * what to do about it, and then — across the floor — why you would trust any
 * of it. That last band is the change: the proof points used to hang in a
 * pane at the lower right, which put three one-line labels in a 380px
 * rectangle sitting on the car's rocker and left the composition weighted into
 * one corner. Across the foot they balance the band instead of decorating it.
 *
 * Nothing here animates the content. The loop is entirely in the backdrop, so
 * the words are on screen and legible in the prerendered HTML before a single
 * frame has run, and they stay that way if the script never arrives at all.
 *
 * Compact catalogue headers keep the dark stage and a reduced image at every
 * width so product listings remain close to the first screen. Other feature
 * pages retain their existing responsive behavior.
 */
export function PageIntroFeature({ highlights, slides, onPhone, compact = false, ...copy }: Props) {
  return (
    <Box
      component="header"
      // One object rather than the array this used to be. The array existed to
      // carry a `theme.applyStyles('dark', ...)` entry — and the cast that entry
      // needed — back when the band was a different colour in each scheme. The
      // stage is scheme-independent now, so there is no second entry to hold,
      // and `sx` goes back to describing the band in one place.
      sx={[
        {
          position: 'relative',
          isolation: 'isolate',
          ...(compact ? { pt: { xs: 3, md: 4 }, pb: { xs: 3, md: 4 } } : BAND_PADDING),
          ...BAND_SURFACE,
          ...BAND_EDGE,
          ...(compact && {
            bgcolor: '#0A0A0C',
            backgroundImage: 'none',
            borderColor: 'rgba(255,255,255,0.12)',
          }),
        },
        // Not drawn on a phone unless the page said to keep it. `display` and
        // not a missing element: the h1 and the trail stay in the document for
        // a crawler and for every width that does draw the band, because the
        // HTML is written once at build time for all of them.
        !onPhone && { display: { xs: 'none', md: 'block' } },
      ]}
    >
      <PageIntroSlideshow slides={slides} />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ maxWidth: COPY_WIDTH }}>
          <PageIntroCopy {...copy} stage compact={compact} solidStage={compact} />
        </Box>
        {highlights && (
          <Box sx={{ mt: STRIP_GAP }}>
            {/* Full width, and sat squarely on the car's lower body. That
                overlap is the point and not a collision: `clear` glass frosts
                whatever is behind it rather than replacing it, so a pane with
                nothing under it is not glass, it is a grey rectangle. The
                wheels and the rocker give the frost something with colour and
                an edge to refract — see the note in PageIntroSlideshow. */}
            <PageIntroTrustStrip items={highlights} stage />
          </Box>
        )}
      </Container>
    </Box>
  );
}
